import {PlusIcon, XIcon} from '@heroicons/react/outline';
import {isEmpty} from 'lodash-es';
import React, {useCallback, useState} from 'react';

import {
  Button,
  CheckBox,
  CountryPicker,
  Input,
  Select,
  SpinningLoader,
} from '@/components';
import type {UserModalProps} from '@/components/DeactivateAccount/types';
import Modal from '@/components/Modals';
import type {AfriexCountryCode, BusinessPoolAccount} from '@/types';
import {AfriexTransactionChannel} from '@/types';
import {countryCodeToCurrencyCodeMap} from '@/utils/countryCodeToCurrencyCodeMap';
import {logRejection} from '@/utils/logRejection';
import {formatSelectOptionItem} from '@/utils/merchant';

import {PoolAccountActionType} from './types';
import usePoolAccountUpdate from './usePoolAccountUpdate';

interface PoolAccountFormModalProps extends Pick<
  UserModalProps,
  'isOpen' | 'onClose'
> {
  initialData?: BusinessPoolAccount;
}
const selectChannels = [
  AfriexTransactionChannel.BANK_ACCOUNT,
  AfriexTransactionChannel.MOBILE_MONEY,
];
const PoolAccountFormModal = ({
  isOpen,
  onClose,
  initialData,
}: PoolAccountFormModalProps) => {
  const {dispatch, isLoading, handleSubmit, poolAccount} = usePoolAccountUpdate(
    onClose,
    initialData,
  );

  const {
    accountName,
    accountNumber,
    countryCode,
    institutionName,
    channel,
    meta,
  } = poolAccount;
  const flags = meta?.flags ?? {};

  const [extraPropsEntries, setExtraPropsEntries] = useState<
    {key: string; value: string}[]
  >(() => {
    const ep = initialData?.meta?.extraProps;
    if (!ep) {
      return [];
    }
    return Object.entries(ep)
      .filter(([, v]) => v != null)
      .map(([key, value]) => ({key, value: String(value)}));
  });

  const syncExtraPropsToMeta = useCallback(
    (entries: {key: string; value: string}[]) => {
      const record: Record<string, unknown> = {};
      entries.forEach(({key, value}) => {
        if (key.trim()) {
          record[key.trim()] = value;
        }
      });
      dispatch({
        type: PoolAccountActionType.UPDATE_POOL_ACCOUNT_EXTRA_PROPS,
        payload: record,
      });
    },
    [dispatch],
  );

  const handleExtraPropChange = useCallback(
    (index: number, field: 'key' | 'value', val: string) => {
      setExtraPropsEntries((prev) => {
        const updated = prev.map((entry, i) =>
          i === index ? {...entry, [field]: val} : entry,
        );
        syncExtraPropsToMeta(updated);
        return updated;
      });
    },
    [syncExtraPropsToMeta],
  );

  const handleAddExtraProp = useCallback(() => {
    setExtraPropsEntries((prev) => [...prev, {key: '', value: ''}]);
  }, []);

  const handleRemoveExtraProp = useCallback(
    (index: number, key: string) => {
      setExtraPropsEntries((prev) => prev.filter((_, i) => i !== index));
      if (key.trim()) {
        dispatch({
          type: PoolAccountActionType.REMOVE_POOL_ACCOUNT_EXTRA_PROP,
          payload: key.trim(),
        });
      }
    },
    [dispatch],
  );

  const updateLabel = isEmpty(initialData)
    ? 'Create Pool Account'
    : 'Update Pool Account';
  const submitLabel = isEmpty(initialData) ? 'Submit' : 'Update';
  const isDisabled =
    isEmpty(poolAccount?.accountName) ||
    isEmpty(poolAccount?.accountNumber) ||
    isLoading ||
    isEmpty(poolAccount?.institutionName) ||
    isEmpty(poolAccount?.countryCode);

  const channelOptions = Object.values(AfriexTransactionChannel)
    .filter((channel) => selectChannels.includes(channel))
    .map((channel) => ({
      label: channel,
      value: channel,
      key: formatSelectOptionItem(channel),
    }));

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        width="w-11/12 sm:w-5/12 md:w-5/12 lg:w-5/12 xl:w-3/12 px-4 sm:px-0"
        closeOnOutsideClick
        title={updateLabel}
        dataCy="merchant-pool-accounts-form"
      >
        <div className="pt-3 px-2">
          {isLoading ? (
            <div className="flex w-full h-full justify-center items-center my-5">
              <SpinningLoader />
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                // `handleSubmit` toasts its own failures, so this only fires if
                // that handler itself throws. Log rather than swallow silently.
                handleSubmit(poolAccount).catch(
                  logRejection('Failed to submit pool account'),
                );
              }}
              className="flex flex-col"
            >
              <Input
                label="Account Name"
                placeholder="Account Name of Pool Account"
                dataCy="merchant-pool-accounts-form-account-name"
                value={accountName || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: PoolAccountActionType.UPDATE_POOL_ACCOUNT,
                    payload: {
                      accountName: e.target?.value,
                    },
                  })
                }
                className="w-full mb-2"
              />

              <Input
                label="Account Number"
                placeholder="Account Number of Pool Account"
                dataCy="merchant-pool-accounts-form-account-number"
                value={accountNumber || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: PoolAccountActionType.UPDATE_POOL_ACCOUNT,
                    payload: {
                      accountNumber: e.target?.value,
                    },
                  })
                }
                className="w-full mb-2"
              />

              <Input
                label="Institution Name"
                placeholder="Bank or Operator Name"
                dataCy="merchant-pool-accounts-form-institution-name"
                value={institutionName || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: PoolAccountActionType.UPDATE_POOL_ACCOUNT,
                    payload: {
                      institutionName: e.target?.value,
                    },
                  })
                }
                className="w-full mb-2"
              />

              <div className="flex flex-col gap-2 mt-2">
                <CountryPicker
                  label="Country"
                  value={countryCode || ''}
                  dataCy="merchant-pool-accounts-form-country"
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    dispatch({
                      type: PoolAccountActionType.UPDATE_POOL_ACCOUNT,
                      payload: {
                        countryCode: e.target?.value as AfriexCountryCode,
                        currencyCode:
                          countryCodeToCurrencyCodeMap[
                            e.target?.value as AfriexCountryCode
                          ],
                      },
                    })
                  }
                  placeholder="--Select Country--"
                  className="mb-2"
                />
              </div>

              <div className="flex flex-col gap-2 mt-2">
                <Select
                  label="Channel"
                  value={channel || ''}
                  dataCy="merchant-pool-accounts-form-channel"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    dispatch({
                      type: PoolAccountActionType.UPDATE_POOL_ACCOUNT,
                      payload: {
                        channel: e.target?.value as AfriexTransactionChannel,
                      },
                    })
                  }
                  options={channelOptions}
                  placeholder="--Select Channel--"
                  className="mb-2"
                />
              </div>

              <div className="mt-4 mb-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Extra Properties
                  </span>
                  <button
                    type="button"
                    onClick={handleAddExtraProp}
                    className="flex items-center gap-1 text-cyan-600 hover:text-cyan-800 text-sm font-medium"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Prop
                  </button>
                </div>
                {extraPropsEntries.map((entry, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 mb-2 w-full"
                  >
                    <div className="min-w-0 flex-1">
                      <Input
                        placeholder="Key"
                        value={entry.key}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleExtraPropChange(index, 'key', e.target?.value)
                        }
                        className="w-full"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <Input
                        placeholder="Value"
                        value={entry.value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleExtraPropChange(index, 'value', e.target?.value)
                        }
                        className="w-full"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveExtraProp(index, entry.key)}
                      className="text-red-500 hover:text-red-700 shrink-0"
                      aria-label="Remove extra prop"
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <CheckBox
                label="Active"
                type="checkbox"
                dataCy="merchant-pool-accounts-form-active"
                checked={Boolean(!flags?.isDeactivated)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: PoolAccountActionType.UPDATE_POOL_ACCOUNT_FLAG,
                    payload: {
                      isDeactivated: !e.target?.checked,
                    },
                  })
                }
              />

              <div className="text-end pb-5">
                <Button
                  disabled={isDisabled}
                  type="submit"
                  variant="solid"
                  colorScheme="cyan"
                  dataCy="merchant-pool-accounts-form-submit"
                >
                  {submitLabel}
                </Button>
              </div>
            </form>
          )}
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default PoolAccountFormModal;
